import React, { useState, useEffect, Suspense } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import appService from '../../../services/ApplicationService';
import userService from '../../../services/UserService';
import companyService from '../../../services/CompanyService';
import ErrorBoundary from '../../common/ErrorBoundery';
import LogoLoader from '../../common/LogoLoader';
import {
    Dashboard as DashboardIcon,
    GridView as GridViewIcon,
    Storage as StorageIcon,
    ManageAccounts as ManageAccountsIcon,
    GppGood as GppGoodIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Lan as LanIcon,
    ChevronRight as ChevronRightIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    ExpandMore as ExpandMoreIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon,
    Business as BusinessIcon,
    Sort as SortIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

const globalStyles = `
    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
    }
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideIn {
        from { transform: translateX(-10px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
    }
    @keyframes progressAnimation {
        0% { width: 0%; }
        100% { width: 100%; }
    }
`;

const DataTable = React.lazy(() => import('../../common/DataGrid'));

const SysOwnerApplications = () => {
    const navigate = useNavigate();
    const [appRecord, setAppRecord] = useState([]);
    const [apps, setApps] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedAppId, setExpandedAppId] = useState(() => {
        // Initialize from localStorage if available
        const saved = localStorage.getItem('expandedAppId');
        return saved ? JSON.parse(saved) : null;
    });
    const [searchTerm, setSearchTerm] = useState(() => {
        // Initialize from localStorage if available
        return localStorage.getItem('appSearchTerm') || '';
    });
    const [selectedStatus, setSelectedStatus] = useState(() => {
        // Initialize from localStorage if available
        return localStorage.getItem('appSelectedStatus') || 'all';
    });
    const [viewMode, setViewMode] = useState(() => {
        // Initialize view mode from localStorage if available
        const savedMode = localStorage.getItem('appViewMode');
        return savedMode || 'flat'; // Default to 'flat' if not found
    });

    // Save search term to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('appSearchTerm', searchTerm);
    }, [searchTerm]);

    // Save selected status to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('appSelectedStatus', selectedStatus);
    }, [selectedStatus]);
    
    // Save view mode to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('appViewMode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = globalStyles;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                
                // Add a 2-second delay before fetching data
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                const [appsResponse, usersResponse, currentUserresponse, companyResponse] = await Promise.all([
                    appService.fetchApps().catch(() => null),
                    userService.fetchUsers().catch(() => null),
                    userService.fetchCurrentUser().catch(() => null),
                    companyService.fetchCompanies().catch(() => null),
                ]);

                if (appsResponse) {
                    setAppRecord(appsResponse);
                } else {
                    setAppRecord([]);
                }

                if (usersResponse) {
                    setUsers(usersResponse);
                } else {
                    setUsers([]);
                }

                if (currentUserresponse) {
                    setCurrentUser(currentUserresponse);
                    
                    const assignedApps = await appService.fetchAppsByOwner(currentUserresponse.id);
                    setApps(assignedApps);
                } else {
                    setCurrentUser(null);
                }

                if (companyResponse) {
                    setCompanies(companyResponse);
                } else {
                    setCompanies([]);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };

        // Add focus event listener to refresh data when returning to the page
        const handleFocus = () => {
            fetchData();
        };

        window.addEventListener('focus', handleFocus);
        fetchData(); // Initial fetch

        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    const handleExpandApp = (appId) => {
        const newExpandedId = expandedAppId === appId ? null : appId;
        setExpandedAppId(newExpandedId);
        // Save to localStorage
        localStorage.setItem('expandedAppId', JSON.stringify(newExpandedId));
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleStatusChange = (event) => {
        setSelectedStatus(event.target.value);
    };

    const toggleViewMode = () => {
        setViewMode(viewMode === 'flat' ? 'grouped' : 'flat');
    };

    const filteredApps = apps.filter(app => {
        const matchesSearch = app.APP_NAME && app.APP_NAME.toLowerCase().includes(searchTerm.toLowerCase());
        const readyForAudit = (app.SETUP_GENINFO || app.geninfo) && 
                              (app.SETUP_USERDATA || app.userdata) && 
                              (app.SETUP_PROCESS || app.process) &&
                              (app.ROLE_OWNER_SETUP || app.roleowner);
        
        if (selectedStatus === 'all') return matchesSearch;
        if (selectedStatus === 'ready') return matchesSearch && readyForAudit;
        if (selectedStatus === 'pending') return matchesSearch && !readyForAudit;
        
        return matchesSearch;
    });

    // Group apps by company
    const groupedApps = React.useMemo(() => {
        const groups = {};
        
        filteredApps.forEach(app => {
            const companyName = app.COMPANY_NAME || 'Unassigned';
            if (!groups[companyName]) {
                groups[companyName] = [];
            }
            groups[companyName].push(app);
        });
        
        // Convert to array format for easier rendering
        return Object.entries(groups).map(([company, apps]) => ({
            company,
            apps,
            readyCount: apps.filter(app => 
                (app.SETUP_GENINFO || app.geninfo) && 
                (app.SETUP_USERDATA || app.userdata) && 
                (app.SETUP_PROCESS || app.process) &&
                (app.ROLE_OWNER_SETUP || app.roleowner)
            ).length
        })).sort((a, b) => a.company.localeCompare(b.company));
    }, [filteredApps]);

    // Compute progress percentage for each app
    const getAppProgress = (app) => {
        let completedSteps = 0;
        const totalSteps = 4; // General Info, User Data, Process Understanding, Role Matrix
        
        if (app.SETUP_GENINFO || app.geninfo) completedSteps++;
        if (app.SETUP_USERDATA || app.userdata) completedSteps++;
        if (app.SETUP_PROCESS || app.process) completedSteps++;
        if (app.ROLE_OWNER_SETUP || app.roleowner) completedSteps++;
        
        return (completedSteps / totalSteps) * 100;
    };

    const getStepStatus = (isComplete) => {
        return isComplete ? (
            <mui.Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: 'success.main',
                fontWeight: 500,
                fontSize: '0.875rem'
            }}>
                <CheckCircleIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Complete
            </mui.Box>
        ) : (
            <mui.Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: 'warning.main',
                fontWeight: 500,
                fontSize: '0.875rem'
            }}>
                <WarningIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Pending
            </mui.Box>
        );
    };
    
    // Return a nice label based on overall progress
    const getReadinessLabel = (progress) => {
        if (progress === 100) return "Audit Ready";
        if (progress >= 75) return "Almost Ready";
        if (progress >= 50) return "In Progress";
        if (progress >= 25) return "Just Started";
        return "Not Started";
    };

    // Render an individual application card
    const renderAppCard = (app) => {
        const progress = getAppProgress(app);
        const isExpanded = expandedAppId === app.id;
        const readinessStatus = getReadinessLabel(progress);
        const isReady = progress === 100;
        
        // Check both naming conventions for each status check
        const hasGenInfo = app.SETUP_GENINFO || app.geninfo;
        const hasUserData = app.SETUP_USERDATA || app.userdata;
        const hasProcess = app.SETUP_PROCESS || app.process;
        const hasRoleMatrix = app.ROLE_OWNER_SETUP || app.roleowner;
        
        return (
            <mui.Paper
                key={app.id}
                elevation={0}
                sx={{
                    overflow: 'hidden',
                    borderRadius: 2,
                    backgroundColor: '#ffffff',
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }
                }}
            >
                <mui.Box 
                    sx={{ 
                        p: 2.5, 
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2
                    }}
                >
                    <mui.Box sx={{ flexGrow: 1 }}>
                        <mui.Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: 600,
                                mb: 0.5,
                                color: 'text.primary',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {app.APP_NAME || 'Unnamed Application'}
                        </mui.Typography>
                        <mui.Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'text.secondary',
                                mb: 1.5
                            }}
                        >
                            {app.APP_DESCRIPTION || 'No description available'} 
                            {viewMode === 'flat' && (
                                <> • <strong>{app.COMPANY_NAME || 'No company'}</strong></>
                            )}
                        </mui.Typography>
                        
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <mui.Box sx={{ flexGrow: 1, bgcolor: '#f0f2f5', borderRadius: 1, height: 8 }}>
                                <mui.Box
                                    sx={{
                                        width: `${progress}%`,
                                        bgcolor: isReady ? 'success.main' : 'primary.main',
                                        height: '100%',
                                        borderRadius: 1,
                                        transition: 'width 1s ease-in-out'
                                    }}
                                />
                            </mui.Box>
                            <mui.Chip
                                label={`${readinessStatus} ${Math.round(progress)}%`}
                                size="small"
                                color={isReady ? "success" : "primary"}
                                sx={{
                                    fontWeight: 500,
                                    minWidth: 100
                                }}
                            />
                        </mui.Box>
                    </mui.Box>
                    
                    <mui.Button
                        onClick={() => handleExpandApp(app.id)}
                        endIcon={
                            <mui.Box
                                component={ExpandMoreIcon}
                                sx={{
                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                                    transition: 'transform 0.3s'
                                }}
                            />
                        }
                        variant="outlined"
                        color={isReady ? "success" : "primary"}
                        sx={{
                            borderRadius: 2,
                            px: 2,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {isExpanded ? 'Hide Details' : 'Setup & Configuration'}
                    </mui.Button>
                </mui.Box>

                <mui.Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <mui.Box sx={{ p: 2.5, pt: 0, animation: 'fadeIn 0.3s ease-out' }}>
                        <mui.Divider sx={{ mb: 2.5 }} />
                        <mui.Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                            Complete these steps to make your application audit ready:
                        </mui.Typography>
                        <mui.Grid container spacing={2.5}>
                            <mui.Grid item xs={12} md={6}>
                                <mui.Card 
                                    variant="outlined" 
                                    sx={{ 
                                        borderRadius: 2,
                                        transition: 'all 0.2s',
                                        height: '100%',
                                        borderColor: hasGenInfo ? 'success.main' : 'divider',
                                        '&:hover': {
                                            borderColor: hasGenInfo ? 'success.main' : 'primary.main',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                        }
                                    }}
                                    onClick={() => navigate(`/applications/setupinfo/${app.id}`)}
                                >
                                    <mui.CardHeader
                                        avatar={
                                            <mui.Avatar sx={{ bgcolor: hasGenInfo ? 'success.main' : 'primary.main' }}>
                                                <StorageIcon />
                                            </mui.Avatar>
                                        }
                                        title="General Information"
                                        subheader="Basic application details and settings"
                                        action={getStepStatus(hasGenInfo)}
                                    />
                                    <mui.CardContent sx={{ pt: 0 }}>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Configure general application information, including environment details, 
                                            application classification, and technical specifications.
                                        </mui.Typography>
                                    </mui.CardContent>
                                    <mui.CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <mui.Button 
                                            size="small" 
                                            endIcon={<ChevronRightIcon />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/applications/setupinfo/${app.id}`);
                                            }}
                                        >
                                            {hasGenInfo ? 'View Details' : 'Configure Now'}
                                        </mui.Button>
                                        {hasGenInfo && (
                                            <mui.Button
                                                size="small"
                                                color="primary"
                                                startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/processnarrative/${app.id}?section=authentication`);
                                                }}
                                                sx={{ ml: 1 }}
                                            >
                                                View Narrative
                                            </mui.Button>
                                        )}
                                    </mui.CardActions>
                                </mui.Card>
                            </mui.Grid>
                            
                            <mui.Grid item xs={12} md={6}>
                                <mui.Card 
                                    variant="outlined" 
                                    sx={{ 
                                        borderRadius: 2,
                                        transition: 'all 0.2s',
                                        height: '100%',
                                        borderColor: hasUserData ? 'success.main' : 'divider',
                                        '&:hover': {
                                            borderColor: hasUserData ? 'success.main' : 'primary.main',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                        }
                                    }}
                                    onClick={() => navigate(`/applications/manageusers/${app.id}`)}
                                >
                                    <mui.CardHeader
                                        avatar={
                                            <mui.Avatar sx={{ bgcolor: hasUserData ? 'success.main' : 'primary.main' }}>
                                                <ManageAccountsIcon />
                                            </mui.Avatar>
                                        }
                                        title="User Data Management"
                                        subheader="Configure users, roles, and permissions"
                                        action={getStepStatus(hasUserData)}
                                    />
                                    <mui.CardContent sx={{ pt: 0 }}>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Manage user data including access control, user permissions, authentication 
                                            methods, and privileged accounts.
                                        </mui.Typography>
                                    </mui.CardContent>
                                    <mui.CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <mui.Button 
                                            size="small" 
                                            endIcon={<ChevronRightIcon />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/applications/manageusers/${app.id}`);
                                            }}
                                        >
                                            {hasUserData ? 'View Details' : 'Configure Now'}
                                        </mui.Button>
                                    </mui.CardActions>
                                </mui.Card>
                            </mui.Grid>
                            
                            <mui.Grid item xs={12} md={6}>
                                <mui.Card 
                                    variant="outlined" 
                                    sx={{ 
                                        borderRadius: 2,
                                        transition: 'all 0.2s',
                                        height: '100%',
                                        borderColor: hasProcess ? 'success.main' : 'divider',
                                        '&:hover': {
                                            borderColor: hasProcess ? 'success.main' : 'primary.main',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                        }
                                    }}
                                    onClick={() => navigate(`/applications/controls/${app.id}`)}
                                >
                                    <mui.CardHeader
                                        avatar={
                                            <mui.Avatar sx={{ bgcolor: hasProcess ? 'success.main' : 'primary.main' }}>
                                                <GppGoodIcon />
                                            </mui.Avatar>
                                        }
                                        title="Process Understanding"
                                        subheader="Document business processes and controls"
                                        action={getStepStatus(hasProcess)}
                                    />
                                    <mui.CardContent sx={{ pt: 0 }}>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Define and document key business processes, workflows, controls, 
                                            risk assessments, and compliance requirements.
                                        </mui.Typography>
                                    </mui.CardContent>
                                    <mui.CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <mui.Button 
                                            size="small" 
                                            endIcon={<ChevronRightIcon />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/applications/controls/${app.id}`);
                                            }}
                                        >
                                            {hasProcess ? 'View Details' : 'Configure Now'}
                                        </mui.Button>
                                        {hasProcess && (
                                            <mui.Button
                                                size="small"
                                                color="primary"
                                                startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/processnarrative/${app.id}?section=provisioning`);
                                                }}
                                                sx={{ ml: 1 }}
                                            >
                                                View Narrative
                                            </mui.Button>
                                        )}
                                    </mui.CardActions>
                                </mui.Card>
                            </mui.Grid>
                            
                            <mui.Grid item xs={12} md={6}>
                                <mui.Card 
                                    variant="outlined" 
                                    sx={{ 
                                        borderRadius: 2,
                                        transition: 'all 0.2s',
                                        height: '100%',
                                        borderColor: hasRoleMatrix ? 'success.main' : 'divider',
                                        '&:hover': {
                                            borderColor: hasRoleMatrix ? 'success.main' : 'primary.main',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                        }
                                    }}
                                    onClick={() => navigate(`/applications/rolematrix/${app.id}`)}
                                >
                                    <mui.CardHeader
                                        avatar={
                                            <mui.Avatar sx={{ bgcolor: hasRoleMatrix ? 'success.main' : 'primary.main' }}>
                                                <LanIcon />
                                            </mui.Avatar>
                                        }
                                        title="Role Matrix"
                                        subheader="Define role-based access control"
                                        action={getStepStatus(hasRoleMatrix)}
                                    />
                                    <mui.CardContent sx={{ pt: 0 }}>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Create and manage role matrices, segregation of duties, access control mapping, 
                                            and role-based permission structures.
                                        </mui.Typography>
                                    </mui.CardContent>
                                    <mui.CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <mui.Button 
                                            size="small" 
                                            endIcon={<ChevronRightIcon />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/applications/rolematrix/${app.id}`);
                                            }}
                                        >
                                            {hasRoleMatrix ? 'View Details' : 'Configure Now'}
                                        </mui.Button>
                                    </mui.CardActions>
                                </mui.Card>
                            </mui.Grid>
                        </mui.Grid>
                    </mui.Box>
                </mui.Collapse>
            </mui.Paper>
        );
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Typography color="text.primary">My Applications</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <GridViewIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                My Applications
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                View and manage the relevant data of the applications assigned to you
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 3,
                        minHeight: '80vh',
                        boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
                        p: 3
                    }}>
                        <mui.Paper 
                            elevation={0} 
                            sx={{ 
                                p: 2, 
                                mb: 3, 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                gap: 2,
                                backgroundColor: '#ffffff',
                                borderRadius: 2
                            }}
                        >
                            <mui.Box sx={{ position: 'relative', flexGrow: 1 }}>
                                <SearchIcon sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary' }} />
                                <mui.InputBase
                                    placeholder="Search applications..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    sx={{
                                        pl: 5,
                                        pr: 2,
                                        py: 1,
                                        width: '100%',
                                        backgroundColor: '#f5f7fa',
                                        borderRadius: 2,
                                        '&:hover': {
                                            backgroundColor: '#eef1f6',
                                        }
                                    }}
                                />
                            </mui.Box>
                            
                            <mui.Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap' }}>
                                <mui.ButtonGroup 
                                    variant="outlined" 
                                    size="small"
                                    sx={{ 
                                        borderRadius: 2,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <mui.Tooltip title="Show all applications">
                                        <mui.Button 
                                            onClick={() => setViewMode('flat')}
                                            variant={viewMode === 'flat' ? 'contained' : 'outlined'}
                                            sx={{ borderRadius: 0 }}
                                        >
                                            <ViewListIcon fontSize="small" />
                                        </mui.Button>
                                    </mui.Tooltip>
                                    <mui.Tooltip title="Group by company">
                                        <mui.Button 
                                            onClick={() => setViewMode('grouped')}
                                            variant={viewMode === 'grouped' ? 'contained' : 'outlined'}
                                            sx={{ borderRadius: 0 }}
                                        >
                                            <BusinessIcon fontSize="small" />
                                        </mui.Button>
                                    </mui.Tooltip>
                                </mui.ButtonGroup>
                            
                                <mui.FormControl sx={{ minWidth: 180 }}>
                                    <mui.Select
                                        value={selectedStatus}
                                        onChange={handleStatusChange}
                                        displayEmpty
                                        size="small"
                                        sx={{ 
                                            backgroundColor: '#f5f7fa',
                                            '& .MuiSelect-select': { 
                                                py: 1,
                                                display: 'flex',
                                                alignItems: 'center'
                                            }
                                        }}
                                        startAdornment={<FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                                    >
                                        <mui.MenuItem value="all">All Applications</mui.MenuItem>
                                        <mui.MenuItem value="ready">Audit Ready</mui.MenuItem>
                                        <mui.MenuItem value="pending">Setup Pending</mui.MenuItem>
                                    </mui.Select>
                                </mui.FormControl>
                            </mui.Box>
                        </mui.Paper>
                        
                        {isLoading ? (
                            <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <LogoLoader 
                                    size={80} 
                                    message="Loading Applications..." 
                                    duration={1.5} 
                                />
                            </mui.Box>
                        ) : filteredApps.length === 0 ? (
                            <mui.Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    backgroundColor: '#ffffff',
                                    borderRadius: 2
                                }}
                            >
                                <mui.Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                                    No applications found
                                </mui.Typography>
                                <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {searchTerm ? "Try adjusting your search criteria" : "No applications have been assigned to you yet"}
                                </mui.Typography>
                            </mui.Paper>
                        ) : (
                            viewMode === 'flat' ? (
                                <mui.Stack spacing={2}>
                                    {filteredApps.map((app) => renderAppCard(app))}
                                </mui.Stack>
                            ) : (
                                <mui.Stack spacing={4}>
                                    {groupedApps.map(({ company, apps, readyCount }) => (
                                        <mui.Box key={company}>
                                            <mui.Box 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    mb: 2,
                                                    pb: 1,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider'
                                                }}
                                            >
                                                <BusinessIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                                                <mui.Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                                    {company}
                                                </mui.Typography>
                                                <mui.Chip 
                                                    label={`${apps.length} ${apps.length === 1 ? 'Application' : 'Applications'}`} 
                                                    size="small"
                                                    color="default"
                                                    variant="outlined"
                                                    sx={{ mr: 1 }}
                                                />
                                                <mui.Chip 
                                                    label={`${readyCount} Audit Ready`} 
                                                    size="small"
                                                    color={readyCount === apps.length ? "success" : "primary"}
                                                    variant={readyCount === apps.length ? "filled" : "outlined"}
                                                />
                                            </mui.Box>
                                            <mui.Stack spacing={2}>
                                                {apps.map((app) => renderAppCard(app))}
                                            </mui.Stack>
                                        </mui.Box>
                                    ))}
                                </mui.Stack>
                            )
                        )}
                    </mui.Box>
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <ErrorBoundary fallback={<p>Failed to load data.</p>} error={error}>
            <Suspense fallback={
                <mui.Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100vh',
                        backgroundColor: '#f8fafc'
                    }}
                >
                    <LogoLoader 
                        size={80} 
                        message="Loading Applications..." 
                        duration={1.5} 
                    />
                </mui.Box>
            }>
        <div>
            <SysOwnerSideBar mainContent={customMainContent} />
        </div>
            </Suspense>
        </ErrorBoundary>
    );
};

export default SysOwnerApplications;
